<template>
    <div class="d-flex p-4 flex-column align-items-center gap-5">
        <TypeAhead
            ref="typeahead"
            v-model:model-value="value"
            :datasources="[datasource]"
            class="form-control-lg flex-fill w-100"
            :ignore-minvalue-on-integer="true"
            :placeholder="'globalsearch.bibcontestantplaceholder'"
            @item-selected="onItemSelected"
            :autoresize="true"
        ></TypeAhead>
    </div>
</template>
<script setup lang="ts">
import { TypeAhead } from '@/components';
import { TypeAheadDataSource } from '@/components/Typeahead';
import { ContestantBase, TypeAheadItem, TypeAheadResponse } from '@/objects/generated';
import { useCommonStoreRef, useContestantStore, useEventCommonStoreRef } from '@/stores';
import { ref } from 'vue';

/**
 * Optional vmodel.
 */
const model = defineModel<TypeAheadItem | null>({ required: false });

const value = ref<string>('');

const { GetDisplayName, SearchContestants } = useContestantStore();

const { Clubs } = useCommonStoreRef();
const { Categories } = useEventCommonStoreRef();

/**
 * The typeahead component
 */
const emit = defineEmits<{
    (e: 'itemSelected', value: TypeAheadItem): void;
}>();

/**
 * The datasource for the typeahead
 */
const datasource = new TypeAheadDataSource('Contains', async (request) => {
    const response = TypeAheadResponse.FromJsValues({
        Items: SearchContestants(request)
            ?.filter((c) => c.RaceContestants && c.RaceContestants.length > 0)
            .map((contestant: ContestantBase) => {
                const raceContestant = contestant.RaceContestants.at(0)!;

                const text = GetDisplayName(raceContestant.ID);
                const category = Categories.value.find((c) => c.ID === contestant.CategoryID);
                const club = Clubs.value.find((c) => c.ID === contestant.ClubID);

                // We show the descript with the category, and then the club if available
                const descriptionParts: string[] = [];
                if (category) descriptionParts.push(category.Name ?? '');
                if (club) descriptionParts.push(club.Name ?? '');

                const item = TypeAheadItem.FromJsValues({
                    DataType: 'Contestant',
                    ID: raceContestant.ID,
                    Text: text,
                    Description: descriptionParts.join(' - '),
                });
                return item;
            }),
    });
    return response;
});

/**
 * When we select an item from the typeahead
 * @param item
 */
const onItemSelected = (item: TypeAheadItem) => {
    model.value = item;
    emit('itemSelected', item);
};
</script>
